import { useEffect, useRef, useState } from "react";
import { api, apiBlob, puede, type Historia as HistoriaApi, type Paciente, type Odontograma } from "../api.ts";
import {
  APP, ALG, FAM, HAB, APP_ALERTA, REGIONES, FINES_DATOS, DERECHOS, normalizarHistoria, aplicarDictado, interpretarAnamnesis, avisoSignos, avisosMedicacion,
  marcasDeCampo, habilesRestantes, aLocal, CAMPOS_ANAMNESIS, CAMPOS_EXAMEN, TIPOS_INFORME, EXAMENES_PEDIDO, diagnosticosFicha, edadConCondicion, partirApellidos, partirNombres,
  type HistoriaEstructurada, type AnamnesisEstructurada, type EstadoRegion, type Derecho,
} from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion, hoy } from "./comun.tsx";
import { useClinica } from "../clinica.ts";
import { leerSesion } from "../api.ts";
import { Dictador } from "../componentes/Dictador.tsx";
import { FirmaPad } from "../componentes/FirmaPad.tsx";
import { AutorizacionDatosParaFirmar, useHojaParaImprimir } from "../componentes/FormularioFirma.tsx";
import { SugerenciaMotivo } from "../componentes/SugerenciaMotivo.tsx";
import { IndicadoresSaludBucal } from "../componentes/IndicadoresSaludBucal.tsx";
import { VisorPdf } from "../componentes/MiFirma.tsx";

type Lista = readonly (readonly [string, RegExp])[];

/** Grupo de casillas de selección (como las del formulario 033). */
function Casillas({ lista, valor, cambiar, tono = "", alerta = [], deshabilitado, etiqueta }: {
  lista: Lista; valor: string[]; cambiar: (v: string[]) => void; tono?: "" | "danger"; alerta?: readonly string[]; deshabilitado: boolean; etiqueta: string;
}) {
  return (
    <div className={`f033-cas ${tono}`} role="group" aria-label={etiqueta} data-lista={etiqueta}>
      {lista.map(([n]) => {
        const on = valor.includes(n);
        return (
          <label key={n} className={`f033-c${alerta.includes(n) ? " f033-alerta" : ""}${on ? " on" : ""}`}>
            <input type="checkbox" checked={on} disabled={deshabilitado} onChange={() => cambiar(on ? valor.filter((x) => x !== n) : [...valor, n])} /> <span>{n}</span>
          </label>
        );
      })}
    </div>
  );
}

/** Tarjeta de la ficha: un recuadro con su título (el color y el tema son los de la app). */
function Tarjeta({ titulo, children, ancho, id }: { titulo: string; letra?: string; children: React.ReactNode; ancho?: boolean; id?: string }) {
  return <fieldset className={`tarjeta-h${ancho ? " span2" : ""}`} id={id}><legend>{titulo}</legend>{children}</fieldset>;
}

/** Casilla suelta (sí / no, SP / CP…). */
function Marca({ marcada, cambiar, deshabilitado, children }: { marcada: boolean; cambiar: (v: boolean) => void; deshabilitado: boolean; children: React.ReactNode }) {
  return <label className={`f033-c${marcada ? " on" : ""}`}><input type="checkbox" checked={marcada} disabled={deshabilitado} onChange={(e) => cambiar(e.target.checked)} /> <span>{children}</span></label>;
}

/** `guardarRef` y `alCambiarSucio` permiten que la ficha guarde la historia desde su botón final y sepa si hay cambios sin guardar. */
export function HistoriaClinica({ pacienteId, paciente, sexo, guardarRef, alCambiarSucio }: { pacienteId: string; paciente?: Paciente; /** sexo que se está escogiendo en la filiación (aunque aún no se guarde) */ sexo?: string; guardarRef?: { current: (() => Promise<unknown>) | null }; alCambiarSucio?: (sucio: boolean) => void }) {
  const [h, setH] = useState<HistoriaEstructurada | null>(null);
  const [sucio, setSucio] = useState(false);
  const [ok, setOk] = useState("");
  const [dictado, setDictado] = useState("");
  const [marcas, setMarcas] = useState<string[]>([]);
  // el odontograma da la dentición (según la edad) y los índices CPO y ceo de los indicadores de salud bucal
  const [odo, setOdo] = useState<Odontograma | null>(null);
  const a = useAccion(), g = useAccion();
  const escribe = puede("clinico:escribir");
  const clinica = useClinica();
  const esHombre = (sexo ?? paciente?.sexo) === "M";
  const sexoAnterior = useRef(sexo);
  // al escoger «Masculino» en la ficha, «¿Embarazada?» pasa a NO (y se quita «Embarazo» de los antecedentes)
  useEffect(() => {
    if (sexo === sexoAnterior.current) return;
    sexoAnterior.current = sexo;
    if (sexo === "M" && h && (h.anamnesis.embarazada !== "no" || h.anamnesis.app.includes("Embarazo"))) {
      setH({ ...h, anamnesis: { ...h.anamnesis, embarazada: "no", app: h.anamnesis.app.filter((x) => x !== "Embarazo") } }); setSucio(true); setOk("");
    }
  }, [sexo, h]);

  useEffect(() => { alCambiarSucio?.(sucio); }, [sucio]);
  useEffect(() => { api<Odontograma>("GET", `/api/pacientes/${pacienteId}/odontograma`).then(setOdo).catch(() => setOdo(null)); }, [pacienteId]);
  useEffect(() => {
    a.correr(() => api<HistoriaApi>("GET", `/api/pacientes/${pacienteId}/historia`)).then((r) => r && setH(normalizarHistoria(r.anamnesis, r.examen)));
  }, [pacienteId]);
  if (!h) return <Seccion titulo="Historia clínica"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;

  const A = h.anamnesis, X = h.examen;
  const setA = (x: Partial<AnamnesisEstructurada>) => { setH({ ...h, anamnesis: { ...A, ...x } }); setSucio(true); setOk(""); };
  const setX = (x: Partial<HistoriaEstructurada["examen"]>) => { setH({ ...h, examen: { ...X, ...x } }); setSucio(true); setOk(""); };
  const setRegion = (k: string, v: EstadoRegion) => setX({ regiones: { ...X.regiones, [k]: v } });
  const cambiarEmbarazo = (v: "" | "si" | "no") => setA({ embarazada: v, app: v === "si" ? [...new Set([...A.app, "Embarazo"])] : A.app.filter((x) => x !== "Embarazo") });
  const cambiarAntecedentes = (v: string[]) => setA({ app: v, embarazada: v.includes("Embarazo") ? "si" : A.embarazada === "si" ? "" : A.embarazada });

  /** Devuelve null si se guardó, o el error. */
  async function guardar(): Promise<unknown> {
    let fallo: unknown = null;
    const r = await g.correr(async () => { try { return await api<HistoriaApi>("PUT", `/api/pacientes/${pacienteId}/historia`, { anamnesis: esHombre && h!.anamnesis.embarazada === "" ? { ...h!.anamnesis, embarazada: "no" } : h!.anamnesis, examen: h!.examen }); } catch (e) { fallo = e; throw e; } });
    if (r) { setH(normalizarHistoria(r.anamnesis, r.examen)); setSucio(false); setOk("Historia clínica guardada."); setMarcas([]); dispatchEvent(new Event("donna:historia")); }
    return fallo;
  }
  if (guardarRef) guardarRef.current = guardar;
  /** Dictado de la historia: marca las casillas y completa el detalle (se revisa y se guarda). */
  function marcarConDictado() {
    const d = interpretarAnamnesis(dictado);
    const nueva = aplicarDictado(h!, { anamnesis: d.anamnesis, examen: d.examen });
    const hechas = [
      ...CAMPOS_ANAMNESIS.flatMap((k) => (d.anamnesis[k] ? marcasDeCampo(k, d.anamnesis[k]!) : [])),
      ...CAMPOS_EXAMEN.flatMap((k) => (d.examen[k] ? marcasDeCampo(k, d.examen[k]!) : [])),
      ...(d.anamnesis.motivo ? ["Motivo de consulta"] : []), ...(d.anamnesis.medicacion ? ["Medicación"] : []),
    ];
    setH(nueva); setSucio(true); setMarcas(hechas.length ? hechas : ["No se reconoció nada: revisa el texto"]); setDictado("");
  }

  const vit = avisoSignos(X.signos);
  const med = avisosMedicacion(A.medicacion);
  const evaluadas = REGIONES.filter((r) => X.regiones[r.k]?.e).length, conPat = REGIONES.filter((r) => X.regiones[r.k]?.e === "CP").length;
  const guardarBtn = escribe && <button className="btn pri" onClick={guardar} disabled={g.ocupado || !sucio}>{g.ocupado ? "Guardando…" : sucio ? "Guardar historia" : "Guardada"}</button>;

  return (
    <Seccion titulo="Historia clínica" acciones={<div className="fila">{sucio && <span className="chip rojo">Cambios sin guardar</span>}{guardarBtn}</div>}>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <AvisoError error={g.error} />
      {escribe && (
        <div className="ex-dict">
          <b style={{ fontSize: 14 }}>Dictar la historia</b>
          <Dictador etiqueta="Dictado de la historia" alto={70} texto={dictado} setTexto={(f) => setDictado(f)}
            placeholder="Ej.: Antecedentes personales hipertensión controlada, toma losartán 50 mg. Alergia a la penicilina. Su madre es diabética. Fuma, se cepilla dos veces al día y usa hilo dental. Presión 130 sobre 85, pulso 76." />
          <div className="fila"><button className="btn" onClick={marcarConDictado} disabled={!dictado.trim()}>Marcar con el dictado</button>
            <span className="muted" style={{ fontSize: 13 }}>Marca las casillas y completa el detalle. Revisa y guarda.</span></div>
          {marcas.length > 0 && <div className="chipset" aria-label="Marcado con el dictado">{marcas.map((m) => <span key={m} className="chip e-confirmada">{m}</span>)}</div>}
        </div>
      )}

      <div className="rejilla-h ficha-compacta">
        <Tarjeta letra="A" titulo="Datos del establecimiento y usuario / paciente" ancho id="datos-033">
          {(() => {
            const pa = paciente;
            const [ape1, ape2] = partirApellidos(pa?.apellidos ?? ""), [nom1, nom2] = partirNombres(pa?.nombres ?? "");
            const ec = pa ? edadConCondicion(pa.nacimiento, hoy()) : null;
            const Dato = ({ t, v, mono }: { t: string; v: string; mono?: boolean }) => <div className="f033-dato"><span className="f033-etq">{t}</span><b className={mono ? "mono" : ""}>{v || "\u00a0"}</b></div>;
            return (
              <>
                <div className="f033-fila">
                  <Dato t="Institución del sistema" v={clinica?.institucion ?? ""} /><Dato t="Unicódigo" v={clinica?.unicodigo ?? ""} mono />
                  <Dato t="Establecimiento de salud" v={clinica?.nombre ?? ""} /><Dato t="N.º de historia clínica única" v={pa?.doc ?? ""} mono /><Dato t="N.º de archivo" v={pa ? String(pa.hc) : ""} mono />
                </div>
                <div className="f033-fila">
                  <Dato t="Primer apellido" v={ape1} /><Dato t="Segundo apellido" v={ape2} /><Dato t="Primer nombre" v={nom1} /><Dato t="Segundo nombre" v={nom2} />
                  <div className="f033-dato"><span className="f033-etq">Sexo</span><span className="fila" style={{ gap: 10 }}><Marca marcada={pa?.sexo === "M"} cambiar={() => undefined} deshabilitado>M</Marca><Marca marcada={pa?.sexo === "F"} cambiar={() => undefined} deshabilitado>F</Marca></span></div>
                  <Dato t="Edad" v={ec ? String(ec.valor) : ""} mono />
                  <div className="f033-dato"><span className="f033-etq">Condición de la edad</span><span className="fila" style={{ gap: 8 }}>{(["H", "D", "M", "A"] as const).map((k) => <Marca key={k} marcada={ec?.condicion === k} cambiar={() => undefined} deshabilitado>{k}</Marca>)}</span></div>
                </div>
                <small className="muted">Salen de la ficha del paciente. La institución y el unicódigo se escriben una vez en Configuración › Empresa y marca.</small>
              </>
            );
          })()}
        </Tarjeta>

        <Tarjeta letra="B" titulo="Motivo de consulta">
          <label className="campo">Motivo de consulta <span className="muted" style={{ fontWeight: 400 }}>(en palabras del paciente)</span>
            <textarea rows={1} value={A.motivo} disabled={!escribe} onChange={(e) => setA({ motivo: e.target.value })} /></label>
          {paciente?.sexo !== "M" && (
            <div className="f033-linea" role="group" aria-label="Embarazada">
              <span className="f033-etq">Embarazada</span>
              <Marca marcada={A.embarazada === "si"} deshabilitado={!escribe} cambiar={(on) => cambiarEmbarazo(on ? "si" : "")}>SÍ</Marca>
              <Marca marcada={A.embarazada === "no" || (esHombre && A.embarazada === "")} deshabilitado={!escribe} cambiar={(on) => cambiarEmbarazo(on ? "no" : "")}>NO</Marca>
            </div>
          )}
        </Tarjeta>

        <Tarjeta letra="C" titulo="Enfermedad actual">
          <label className="campo"><span className="sr">Enfermedad actual</span>
            <textarea rows={2} aria-label="Enfermedad actual" value={A.enfermedad_actual} disabled={!escribe} onChange={(e) => setA({ enfermedad_actual: e.target.value })} /></label>
          {escribe && (
            <SugerenciaMotivo pacienteId={pacienteId} motivo={A.motivo} enfermedad={A.enfermedad_actual}
              alUsar={(t, modo) => setA({ enfermedad_actual: modo === "agregar" && A.enfermedad_actual.trim() ? `${A.enfermedad_actual.trim().replace(/([^.!?])$/, "$1.")} ${t}` : t })} />
          )}
        </Tarjeta>

        <Tarjeta letra="D" titulo="Antecedentes patológicos personales">
          <Casillas etiqueta="Antecedentes patológicos personales" lista={APP} valor={A.app} alerta={APP_ALERTA} deshabilitado={!escribe} cambiar={cambiarAntecedentes} />
          <label className="campo">Detalle, cirugías y hospitalizaciones<textarea rows={2} value={A.app_det} disabled={!escribe} onChange={(e) => setA({ app_det: e.target.value })} /></label>
        </Tarjeta>

        <Tarjeta titulo="Alergias">
          <label className="niega"><input type="checkbox" checked={A.alg_niega} disabled={!escribe || A.alg.length > 0} onChange={(e) => setA({ alg_niega: e.target.checked })} /> Niega alergias conocidas</label>
          <Casillas etiqueta="Alergias" tono="danger" lista={ALG} valor={A.alg} deshabilitado={!escribe} cambiar={(v) => setA({ alg: v, alg_niega: v.length ? false : A.alg_niega })} />
          <label className="campo">Otra alergia o reacción<input className="entrada" value={A.alg_otra} disabled={!escribe} onChange={(e) => setA({ alg_otra: e.target.value, alg_niega: e.target.value ? false : A.alg_niega })} /></label>
        </Tarjeta>

        <Tarjeta titulo="Medicación actual">
          <label className="campo"><span className="sr">Medicación actual</span>
            <textarea rows={3} aria-label="Medicación actual" placeholder="Nombre, dosis y frecuencia" value={A.medicacion} disabled={!escribe} onChange={(e) => setA({ medicacion: e.target.value })} /></label>
          {med.length > 0 && <small className="hint warn">Atención: {med.join("; ")}.</small>}
        </Tarjeta>

        <Tarjeta letra="E" titulo="Antecedentes patológicos familiares">
          <Casillas etiqueta="Antecedentes patológicos familiares" lista={FAM} valor={A.fam} deshabilitado={!escribe} cambiar={(v) => setA({ fam: v })} />
          <label className="campo">Parentesco y detalle<input className="entrada" value={A.fam_det} disabled={!escribe} onChange={(e) => setA({ fam_det: e.target.value })} /></label>
        </Tarjeta>

        <Tarjeta titulo="Hábitos e higiene oral">
          <Casillas etiqueta="Hábitos" lista={HAB} valor={A.hab} deshabilitado={!escribe} cambiar={(v) => setA({ hab: v })} />
          <label className="campo">Detalle de hábitos <span className="muted" style={{ fontWeight: 400 }}>(cantidad, frecuencia)</span><input className="entrada" value={A.hab_det} disabled={!escribe} onChange={(e) => setA({ hab_det: e.target.value })} /></label>
          <div className="fila" style={{ alignItems: "end" }}>
            <label className="campo" style={{ minWidth: 160 }}>Cepillado al día
              <select className="entrada" value={A.cepillado} disabled={!escribe} onChange={(e) => setA({ cepillado: e.target.value as AnamnesisEstructurada["cepillado"] })}>
                <option value="">Sin registrar</option><option value="0">No se cepilla</option><option value="1">1 vez</option><option value="2">2 veces</option><option value="3">3 o más</option>
              </select></label>
            <label className="niega"><input type="checkbox" checked={A.hilo} disabled={!escribe} onChange={(e) => setA({ hilo: e.target.checked })} /> Usa hilo dental</label>
            <label className="niega"><input type="checkbox" checked={A.enjuague} disabled={!escribe} onChange={(e) => setA({ enjuague: e.target.checked })} /> Usa enjuague</label>
          </div>
        </Tarjeta>

        <Tarjeta letra="F" titulo="Constantes vitales">
          <div className="vitals">
            <label className="campo">Presión arterial <span className="muted">mmHg</span>
              <span className="pa"><input className="entrada mono" inputMode="numeric" aria-label="Sistólica" value={X.signos.pa_s} disabled={!escribe} onChange={(e) => setX({ signos: { ...X.signos, pa_s: e.target.value.replace(/\D/g, "").slice(0, 3) } })} />
                <span>/</span><input className="entrada mono" inputMode="numeric" aria-label="Diastólica" value={X.signos.pa_d} disabled={!escribe} onChange={(e) => setX({ signos: { ...X.signos, pa_d: e.target.value.replace(/\D/g, "").slice(0, 3) } })} /></span></label>
            <label className="campo">Frec. cardíaca <span className="muted">lpm</span><input className="entrada mono" inputMode="numeric" value={X.signos.fc} disabled={!escribe} onChange={(e) => setX({ signos: { ...X.signos, fc: e.target.value.replace(/\D/g, "").slice(0, 3) } })} /></label>
            <label className="campo">Frec. respiratoria <span className="muted">rpm</span><input className="entrada mono" inputMode="numeric" value={X.signos.fr} disabled={!escribe} onChange={(e) => setX({ signos: { ...X.signos, fr: e.target.value.replace(/\D/g, "").slice(0, 2) } })} /></label>
            <label className="campo">Temperatura <span className="muted">°C</span><input className="entrada mono" inputMode="decimal" value={X.signos.temp} disabled={!escribe} onChange={(e) => setX({ signos: { ...X.signos, temp: e.target.value.replace(/[^\d.,]/g, "").slice(0, 4) } })} /></label>
          </div>
          {vit.texto && <small className={`hint ${vit.nivel}`}>{vit.texto}</small>}
        </Tarjeta>

        <Tarjeta letra="G" titulo="Examen del sistema estomatognático" ancho id="examen">
          <div className="ex-head">
            <p className="muted" style={{ margin: 0 }}><b className="mono">{evaluadas}</b> de {REGIONES.length} regiones evaluadas{conPat > 0 && <> · <span style={{ color: "var(--red)" }}>{conPat} con patología</span></>}</p>
            {escribe && <button type="button" className="btn chico" disabled={evaluadas === REGIONES.length}
              onClick={() => setX({ regiones: Object.fromEntries(REGIONES.map((r) => [r.k, X.regiones[r.k]?.e ? X.regiones[r.k] : { e: "SP", h: [], nota: "" }])) })}>Marcar pendientes sin patología</button>}
          </div>
          {escribe && <DictadoExamen alLlenar={(r) => {
            const regiones = { ...X.regiones };
            for (const [k, v] of Object.entries(r.regiones)) regiones[k] = v;
            if (r.resto) for (const reg of REGIONES) if (!regiones[reg.k]?.e) regiones[reg.k] = { e: "SP", h: [], nota: "" };
            setX({ regiones, apertura: r.apertura ?? X.apertura });
            return Object.keys(r.regiones).length + (r.resto ? REGIONES.filter((reg) => !X.regiones[reg.k]?.e && !r.regiones[reg.k]).length : 0);
          }} />}
          <div className="ex-list">
            {REGIONES.map((r, i) => {
              const v = X.regiones[r.k] ?? { e: null, h: [], nota: "" };
              return (
                <div key={r.k} className={`ex-row${v.e === "CP" ? " cp" : ""}${!v.e ? " pend" : ""}`}>
                  <div className="ex-top">
                    <span className="ex-n mono">{i + 1}</span>
                    <span className="ex-name">{r.n}</span>
                    <div className="f033-sp" role="group" aria-label={r.n}>
                      <Marca marcada={v.e === "SP"} deshabilitado={!escribe} cambiar={() => setRegion(r.k, v.e === "SP" ? { e: null, h: [], nota: "" } : { e: "SP", h: [], nota: "" })}>SP · Sin patología</Marca>
                      <Marca marcada={v.e === "CP"} deshabilitado={!escribe} cambiar={() => setRegion(r.k, v.e === "CP" ? { e: null, h: [], nota: "" } : { e: "CP", h: v.h, nota: v.nota })}>CP · Con patología</Marca>
                    </div>
                  </div>
                  {v.e === "CP" && (
                    <div className="ex-body">
                      <div className="f033-cas" role="group" aria-label={`Hallazgos en ${r.n}`}>
                        {r.o.map(([n, cie]) => (
                          <Marca key={n} marcada={v.h.includes(n)} deshabilitado={!escribe}
                            cambiar={() => setRegion(r.k, { ...v, h: v.h.includes(n) ? v.h.filter((x) => x !== n) : [...v.h, n] })}>{n} <span className="mono cc">{cie}</span></Marca>
                        ))}
                      </div>
                      {r.k === "atm" && (
                        <div className="fila" style={{ gap: 8 }}>
                          <label className="muted" htmlFor="f-apertura" style={{ fontSize: 13 }}>Apertura máxima</label>
                          <input id="f-apertura" className="entrada mono ap" inputMode="numeric" value={X.apertura} disabled={!escribe} onChange={(e) => setX({ apertura: e.target.value.replace(/\D/g, "").slice(0, 2) })} /><span className="muted">mm</span>
                          {Number(X.apertura) > 0 && Number(X.apertura) < 40 && <small className="hint warn">Apertura reducida (&lt; 40 mm)</small>}
                        </div>
                      )}
                      <input className="entrada ex-nota" aria-label={`Descripción en ${r.n}`} placeholder="Describe: localización, tamaño, color, consistencia…" value={v.nota} disabled={!escribe}
                        onChange={(e) => setRegion(r.k, { ...v, nota: e.target.value })} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {X.periodontal.trim() && (
            <p className="muted" style={{ margin: "10px 0 0", fontSize: 13 }}>
              <b>Periodonto (registro anterior):</b> {X.periodontal}{" "}
              {escribe && <button type="button" className="enlace" onClick={() => setX({ periodontal: "" })}>Quitar este texto</button>}
            </p>
          )}
        </Tarjeta>

        <Tarjeta letra="H" titulo="Odontograma">
          <p className="muted" style={{ margin: 0 }}>El odontograma con la simbología del 033 (caries, obturaciones, ausencias, prótesis, recesión y movilidad) se llena en su pestaña; de ahí salen los diagnósticos con CIE-10 y el plan de tratamiento.</p>
          <div><button type="button" className="btn chico" onClick={() => { location.hash = `#/paciente/${pacienteId}/odontograma`; }}>Abrir el odontograma</button></div>
        </Tarjeta>

        <Tarjeta letra="I" titulo="Indicadores de salud bucal y J. Índices CPO-ceo" ancho id="indicadores">
          <IndicadoresSaludBucal valor={X.indicadores} alCambiar={(v) => setX({ indicadores: v })} escribe={escribe} odo={odo} />
        </Tarjeta>

        <Tarjeta letra="L" titulo="Pedido de exámenes complementarios" id="pedido-examenes">
          <Casillas etiqueta="Exámenes pedidos" lista={EXAMENES_PEDIDO.map((e) => [e, /(?!)/] as const)} valor={X.complementarios.pedido_lista} deshabilitado={!escribe}
            cambiar={(v) => setX({ complementarios: { ...X.complementarios, pedido_lista: v } })} />
          <label className="campo">Otros exámenes o indicaciones
            <textarea rows={2} value={X.complementarios.pedido} disabled={!escribe} onChange={(e) => setX({ complementarios: { ...X.complementarios, pedido: e.target.value } })} /></label>
        </Tarjeta>

        <Tarjeta letra="M" titulo="Informe de exámenes" id="informe-examenes">
          <Casillas etiqueta="Exámenes informados" lista={TIPOS_INFORME.map((e) => [e, /(?!)/] as const)} valor={X.complementarios.informe_tipos} deshabilitado={!escribe}
            cambiar={(v) => setX({ complementarios: { ...X.complementarios, informe_tipos: v as typeof X.complementarios.informe_tipos } })} />
          {X.complementarios.informe_tipos.includes("Otros") && (
            <label className="campo">¿Cuál es el otro examen?<input className="entrada" value={X.complementarios.informe_otros} maxLength={200} disabled={!escribe} onChange={(e) => setX({ complementarios: { ...X.complementarios, informe_otros: e.target.value } })} /></label>
          )}
          <label className="campo">Resultado
            <textarea rows={3} value={X.complementarios.informe} disabled={!escribe} onChange={(e) => setX({ complementarios: { ...X.complementarios, informe: e.target.value } })} /></label>
        </Tarjeta>

        <Tarjeta letra="N" titulo="Diagnóstico (CIE-10)" ancho id="diagnosticos">
          {(() => {
            const dx = odo ? diagnosticosFicha(odo.hallazgos, X.regiones) : [];
            const fijar = (cie: string, e: "PRE" | "DEF", on: boolean) => {
              const n = { ...X.dx_estado };
              if (on) n[cie] = e; else delete n[cie];
              setX({ dx_estado: n });
            };
            return dx.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>Aún no hay diagnósticos. Salen solos del odontograma, del examen estomatognático y de los indicadores de salud bucal.</p>
            ) : (
              <>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>PRE = presuntivo · DEF = definitivo. Los diagnósticos se arman solos; aquí solo marcas si cada uno es presuntivo o definitivo.</p>
                <table className="f033-tabla">
                  <thead><tr><th>N.º</th><th>Diagnóstico</th><th>CIE-10</th><th>PRE</th><th>DEF</th></tr></thead>
                  <tbody>
                    {dx.map((d, i) => (
                      <tr key={`${d.cie}-${d.donde}-${i}`}>
                        <td className="c">{i + 1}</td><td>{d.texto} <span className="muted">({d.donde})</span></td><td className="c mono">{d.cie}</td>
                        <td className="c"><Marca marcada={X.dx_estado[d.cie] === "PRE"} deshabilitado={!escribe} cambiar={(on) => fijar(d.cie, "PRE", on)}><span className="sr">Presuntivo {d.cie}</span></Marca></td>
                        <td className="c"><Marca marcada={X.dx_estado[d.cie] === "DEF"} deshabilitado={!escribe} cambiar={(on) => fijar(d.cie, "DEF", on)}><span className="sr">Definitivo {d.cie}</span></Marca></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            );
          })()}
        </Tarjeta>

        <Tarjeta letra="O" titulo="Datos del profesional responsable">
          <div className="f033-fila">
            <div className="f033-dato"><span className="f033-etq">Profesional</span><b>{leerSesion()?.usuario.nombre ?? ""}</b></div>
            <div className="f033-dato"><span className="f033-etq">Registro</span><b className="mono">{leerSesion()?.usuario.registro ?? ""}</b></div>
          </div>
          <small className="muted">El tratamiento por sesión (sección P) sale de las notas de evolución firmadas, cada una con la firma electrónica de quien la firmó.</small>
        </Tarjeta>

        <Tarjeta titulo="Protección de datos personales" ancho><DatosPersonales pacienteId={pacienteId} paciente={paciente} /></Tarjeta>
      </div>
    </Seccion>
  );
}

function DictadoExamen({ alLlenar }: { alLlenar: (r: { regiones: Record<string, EstadoRegion>; resto: boolean; apertura: string | null }) => number }) {
  const [t, setT] = useState("");
  const [res, setRes] = useState("");
  const a = useAccion();
  const EJEMPLO = "Labios sin patología. Lengua saburral en el tercio posterior. Paladar con torus palatino de un centímetro, asintomático. Carrillos con línea alba bilateral. ATM con chasquido en apertura del lado izquierdo, apertura máxima 38 milímetros. Ganglio submandibular derecho palpable, móvil y doloroso. Resto sin patología.";
  async function llenar(texto = t) {
    const r = await a.correr(() => api<{ motor: string; regiones: Record<string, EstadoRegion>; resto: boolean; apertura: string | null }>("POST", "/api/dictado/examen", { texto }));
    if (r) { const n = alLlenar(r); setRes(`Completado con ${r.motor === "ia" ? "IA" : "las reglas de Donna"}: ${n} región${n === 1 ? "" : "es"}. Revisa el examen y guarda.`); }
  }
  return (
    <div className="ex-dict">
      <b style={{ fontSize: 14 }}>Dictado del examen</b>
      <Dictador etiqueta="Dictado del examen" alto={60} texto={t} setTexto={(f) => setT(f)} placeholder="Ej.: Lengua saburral. Torus palatino. Resto sin patología." />
      <div className="fila">
        <button type="button" className="btn chico" onClick={() => { setT(EJEMPLO); llenar(EJEMPLO); }} disabled={a.ocupado}>Ejemplo de examen</button>
        <button type="button" className="btn" onClick={() => llenar()} disabled={a.ocupado || !t.trim()}>{a.ocupado ? "Leyendo el examen…" : "Llenar examen con el dictado"}</button>
        {res && <span className="muted" style={{ fontSize: 13 }}>{res}</span>}
      </div>
      <AvisoError error={a.error} />
    </div>
  );
}

/* ---------------- Protección de datos personales ---------------- */

interface EstadoDatos {
  actual: { id: string; estado: string; fines: Record<string, boolean>; firmante: string; calidad: string; creado: string; por: string; en_papel?: boolean; firmado_digital?: boolean } | null;
  solicitudes: { id: string; tipo: Derecho; detalle: string; recibida: string; vence: string; estado: string; respuesta: string; respondida_en: string | null; respondida_por: string | null }[];
  accesos: { creado: string; accion: string; nombre: string | null }[] | null;
  firmante: string; calidad: string; menor: boolean; hoy: string;
}
const fechaCorta = (iso: string) => { const d = new Date(iso.length === 10 ? iso + "T12:00:00-05:00" : iso); return d.toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric", timeZone: "America/Guayaquil" }); };
const momento = (iso: string) => { const l = aLocal(new Date(iso)); const [, m, d] = l.fecha.split("-"); return `${d}/${m} ${l.hora}`; };

/** Mensaje de lo registrado; avisa solo cuando la firma digital falló o venció (quien no tiene firma cargada no recibe avisos). */
const avisoFirma = (base: string, f?: string) => f === "firmado" ? `${base} y firmado digitalmente.` : f === "vencida" ? `${base}. Tu firma electrónica está vencida: salió sin firma digital.` : f === "error" ? `${base}. No se pudo firmar digitalmente el documento.` : `${base}.`;

function DatosPersonales({ pacienteId, paciente }: { pacienteId: string; paciente?: Paciente }) {
  const [e, setE] = useState<EstadoDatos | null>(null);
  const [fines, setFines] = useState<Record<string, boolean>>({ atencion: true, recordatorios: true, promociones: true, fotos: true });
  const [firma, setFirma] = useState<string | null>(null);
  const [tipo, setTipo] = useState<Derecho>("acceso");
  const [msg, setMsg] = useState("");
  const [pdf, setPdf] = useState<string | null>(null);
  const [hojaParaFirmar, imprimirParaFirmar] = useHojaParaImprimir();
  const a = useAccion();
  const escribe = puede("pacientes:escribir"), clinico = puede("clinico:leer");
  const url = `/api/pacientes/${pacienteId}/datos-personales`;
  const cargar = async () => { const r = await a.correr(() => api<EstadoDatos>("GET", url)); if (r) { setE(r); if (r.actual) setFines(r.actual.fines); } };
  useEffect(() => { cargar(); }, [pacienteId]);
  if (!e) return <><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</>;
  const vigente = e.actual && e.actual.estado !== "revocado";

  async function firmar() {
    const r = await a.correr(() => api<{ firma_digital?: string }>("POST", `${url}/consentimiento`, { fines, firma }));
    if (r) { setMsg(avisoFirma("Consentimiento registrado", r.firma_digital)); setFirma(null); cargar(); }
  }
  async function cambiarFin(k: string, v: boolean) {
    const nuevos = { ...fines, [k]: v };
    setFines(nuevos);
    const r = vigente ? await a.correr(() => api<{ firma_digital?: string }>("POST", `${url}/consentimiento`, { fines: nuevos })) : undefined;
    if (r) { setMsg(avisoFirma("Autorización actualizada", r.firma_digital)); cargar(); }
  }
  async function revocar() {
    if (!confirm("¿Registrar la revocatoria? Se detienen los recordatorios, las promociones y las fotografías. Los datos clínicos se conservan por obligación legal.")) return;
    const r = await a.correr(() => api<{ firma_digital?: string }>("POST", `${url}/revocar`));
    if (r) { setMsg(avisoFirma("Consentimiento revocado: se detienen recordatorios y promociones", r.firma_digital)); cargar(); }
  }
  async function solicitud() {
    const r = await a.correr(() => api<{ vence: string }>("POST", `${url}/solicitudes`, { tipo }));
    if (r) {
      setMsg(tipo === "eliminacion" ? "Solicitud registrada. La historia clínica debe conservarse por ley; se pueden eliminar datos de contacto comercial y fotos no clínicas."
        : tipo === "acceso" || tipo === "portabilidad" ? `Solicitud registrada (vence el ${fechaCorta(r.vence)}). Responde con «Exportar datos del paciente».`
        : `Solicitud registrada: vence el ${fechaCorta(r.vence)}.`);
      cargar();
    }
  }
  async function responder(id: string) {
    const respuesta = prompt("¿Cómo se respondió la solicitud?", "Se entregó la información al titular.");
    if (respuesta === null) return;
    if (await a.correr(() => api("PUT", `${url}/solicitudes/${id}`, { respuesta }))) cargar();
  }
  async function exportar() {
    await a.correr(async () => {
      const enlace = document.createElement("a");
      enlace.href = await apiBlob(`/api/pacientes/${pacienteId}/exportar`);
      enlace.download = `datos-paciente-${e!.hoy}.json`; enlace.click();
      setMsg("Datos del paciente exportados (archivo JSON)."); cargar();
    });
  }

  return (
    <div className="datos-personales">
      {hojaParaFirmar}
      {pdf && <VisorPdf ruta={pdf} titulo="Autorización de datos personales" alCerrar={() => setPdf(null)} />}
      <div className={`priv-st ${vigente ? "ok" : "due"}`}>
        <b>{vigente ? "Consentimiento de datos firmado" : e.actual?.estado === "revocado" ? "Consentimiento de datos revocado" : "Falta el consentimiento de datos personales"}</b>
        <span>{e.actual ? `${fechaCorta(e.actual.creado)} · ${e.actual.firmante} (${e.actual.calidad})${e.actual.en_papel ? " · firmado en papel" : ""}${e.actual.estado === "actualizado" ? " · autorizaciones actualizadas" : ""}`
          : `Los datos de salud son sensibles: la LOPDP exige autorización expresa del ${e.menor ? "representante legal" : "titular"}.`}</span>
      </div>
      {e.actual?.firmado_digital && <div><button className="btn chico" onClick={() => setPdf(`/api/documentos/datos/${e.actual!.id}/pdf`)}>Abrir PDF firmado digitalmente</button></div>}
      <div className="ft-aut">
        {FINES_DATOS.map(([k, t]) => (
          <label key={k}><input type="checkbox" checked={k === "atencion" || !!fines[k]} disabled={k === "atencion" || !escribe || a.ocupado} onChange={(x) => cambiarFin(k, x.target.checked)} /> {t}</label>
        ))}
      </div>
      {paciente && (
        <div className="fila">
          <button className="btn chico" onClick={() => imprimirParaFirmar(<AutorizacionDatosParaFirmar paciente={paciente} menor={e.menor}
            representante={paciente.representante?.nombre ? { nombre: paciente.representante.nombre, doc: paciente.representante.doc ?? "" } : null} />)}
            title="Hoja en blanco, con casillas para cada autorización, para que el paciente la firme a mano">Imprimir autorización para firmar</button>
          <span className="muted" style={{ fontSize: 12 }}>Para firmar en papel: protección de datos y mensajes (WhatsApp y correo).</span>
        </div>
      )}
      {escribe && !vigente && (
        <div className="firma-lopdp">
          <span className="muted" style={{ fontSize: 13 }}>Firma del {e.menor ? "representante legal" : "titular"}: <b>{e.firmante || (e.menor ? "(registra al representante en la filiación)" : "")}</b></span>
          <FirmaPad alCambiar={setFirma} />
          <div><button className="btn chico pri" onClick={firmar} disabled={a.ocupado || !firma || !e.firmante}>Registrar consentimiento</button></div>
        </div>
      )}
      {escribe && vigente && <div><button className="enlace" onClick={revocar}>Revocar consentimiento</button></div>}
      {msg && <div className="aviso ok" role="status">{msg}</div>}
      <AvisoError error={a.error} />

      <div className="eyebrow">Derechos del titular</div>
      <div className="fila">
        {escribe && <>
          <select className="entrada" style={{ width: "auto" }} aria-label="Tipo de solicitud" value={tipo} onChange={(x) => setTipo(x.target.value as Derecho)}>
            {Object.entries(DERECHOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button className="btn chico" onClick={solicitud} disabled={a.ocupado}>Registrar solicitud</button>
        </>}
        {tipo === "eliminacion" && <p className="muted" style={{ margin: "4px 0 0", fontSize: 13, flexBasis: "100%" }}>La historia clínica no se puede eliminar aunque el paciente lo pida: la ley (MSP y LOPDP) obliga a conservarla 15 años desde su última atención. Donna no tiene opción para borrarla; se puede responder la solicitud explicándolo.</p>}
        {clinico && <button className="btn chico" onClick={exportar} disabled={a.ocupado}>Exportar datos del paciente</button>}
      </div>
      {e.solicitudes.length > 0 && (
        <ul className="preguntas">
          {e.solicitudes.map((s) => {
            const r = habilesRestantes(e.hoy, s.vence);
            return (
              <li key={s.id}><b>{DERECHOS[s.tipo]}</b> <span className="muted">· recibida {fechaCorta(s.recibida)} · vence {fechaCorta(s.vence)}</span>{" "}
                {s.estado === "respondida" ? <span className="chip e-confirmada">Respondida</span>
                  : <><span className={`chip ${r < 0 ? "rojo" : r <= 3 ? "rojo" : ""}`}>{r < 0 ? "Vencida" : `${r} días hábiles`}</span> {escribe && <button className="enlace" onClick={() => responder(s.id)}>Marcar respondida</button>}</>}
                {s.respuesta && <div className="muted" style={{ fontSize: 12 }}>{s.respuesta}</div>}
              </li>
            );
          })}
        </ul>
      )}
      {e.accesos && <>
        <div className="eyebrow">Últimos accesos a esta ficha</div>
        <div className="aud">{e.accesos.length ? e.accesos.map((x, i) => <span key={i}><time className="mono">{momento(x.creado)}</time> {x.nombre ?? "Sistema"} · {x.accion}</span>) : <span className="muted">Sin registros.</span>}</div>
      </>}
    </div>
  );
}
